const _ = require("lodash");
const {
  format,
  isSaturday,
  isSunday,
  subDays,
  parse,
  isValid,
} = require("date-fns");

const { preloadFiles } = require("./preloadFiles");

const DATE_FORMAT = "yyyy-MM-dd";

const marketUtility = (market) => {
  const findMarketConfig = (symbol = "") => {
    return _.find(market.configs, { symbol }) || {};
  };

  const getExpiryDates = _.memoize(
    (args) => {
      const {
        symbol,
        expiryType = "*",
        filterFn = (data) => data,
      } = args || {};
      const config = findMarketConfig(symbol);
      if (_.isEmpty(config)) return [];

      return _.chain(config.expiry)
        .keys()
        .filter((key) => key === expiryType || expiryType === "*")
        .map((key) => {
          return preloadFiles?.[config.expiry[key]];
        })
        .flatten()
        .uniq()
        .sort()
        .thru(filterFn)
        .value();
    },
    ({ symbol, expiryType, filterFn, uniqueValue = "__uniqueValue__" }) =>
      `${symbol}-${expiryType}-${filterFn?.toString()}-${uniqueValue}`
  );

  const getSymbolConfig = _.memoize(
    (args) => {
      const {
        symbol,
        date = undefined,
        expiryTypes = [],
        filterFn = (data) => data,
        attributeFilterFn = (keys, date) =>
          _.findLast(keys, (d) => !date || d <= date),
      } = args || {};

      const config = findMarketConfig(symbol);
      if (_.isEmpty(config)) return {};

      const { dateBasedAttributes } = config;

      return {
        ...config,
        expiry: {
          weekly: getExpiryDates({ symbol, expiryType: "weekly", filterFn }),
          monthly: getExpiryDates({ symbol, expiryType: "monthly", filterFn }),
        },
        attributes: _.chain(dateBasedAttributes)
          .reduce((acc, value, key) => { 
            if (!value.filter) acc[key] = value;
            if (_.some(expiryTypes, (type) => value.filter?.includes(type))) {
              acc[key] = value;
            }

            return acc;
          }, {})
          .keys()
          .sort()
          .thru((keys) => attributeFilterFn(keys, date))
          .thru((selectedDate) => dateBasedAttributes[selectedDate] || {})
          .value(),
      };
    },
    ({ symbol, date, filterFn, attributeFilterFn, uniqueValue = "__uniqueValue__", expiryTypes }) =>
      `${symbol}-${date}-${expiryTypes}-${filterFn?.toString()}-${attributeFilterFn?.toString()}-${uniqueValue}`
  );

  const getValidMarketDate = (args) => {
    const { date = "" } = args || {};
    const actualDate = parse(date, DATE_FORMAT, new Date());
    // Check for invalid date
    if (!isValid(actualDate)) {
      return "Invalid date";
    }

    let validDate = actualDate;

    while (true) {
      const formattedDate = format(validDate, DATE_FORMAT);

      if (
        market.holidayDates.includes(formattedDate) ||
        ((isSaturday(validDate) || isSunday(validDate)) &&
          !market.weekendWorkingDates.includes(formattedDate))
      ) {
        validDate = subDays(validDate, 1);
      } else {
        break;
      }
    }

    return format(validDate, DATE_FORMAT);
  };

  const getSymbols = (args) => {
    const {
      marketType = "*",
      isIndex = "*",
      filterFn = (data) => data,
    } = args || {};

    return _.chain(market.configs)
      .filter((config) => {
        return (
          (marketType === "*" || config.market === marketType) &&
          (isIndex === "*" || config.isIndex === isIndex)
        );
      })
      .thru(filterFn)
      .map("symbol")
      .value();
  };

  return {
    getExpiryDates,
    getSymbolConfig,
    getValidMarketDate,
    getSymbols,
  };
};

module.exports = marketUtility;